import { PendingSubmissionModel, SubmissionModel, UnsavedSubmissionModel, isPending } from "../../reduxToolkit/submissionsSlice"
import { IncidentModel } from "../../reduxToolkit/incidentsSlice"
import { ExpenseModel } from "../../reduxToolkit/expensesSlice"
import { Customizations } from "../../reduxToolkit/selectors/customizations"
import { chooseSubmissionType, MinistryContext } from "../rules/chooseSubmissionType"
import { SubmissionType } from "../../types/supabase"

/**
 * Returns the incident's existing pending submission, or builds an unsaved one of
 * the type the rules currently call for. Returns null when no submission is due.
 *
 * Moved out of app/hooks/usePendingSubmission so that lib/todos/incident.ts can
 * use it without importing from app/hooks -- a lib -> app/hooks dependency becomes
 * a circular package dependency once the todo generators move into
 * @healthshare/shared (Phase 1 of docs/plans/embeddable-sdk.md) while the hooks
 * stay in apps/web. Despite living in a hooks file, this was never a hook: it is a
 * pure function of the state handed to it.
 *
 * app/hooks/usePendingSubmission re-exports it so existing imports keep resolving.
 */
export function getOrBuildPendingSubmissionModel(
  {
    incident,
    incidentSubmissions,
    incidentExpenses,
    allMembershipSubmissions,
    allMembershipIncidents,
  }: {
    incident: IncidentModel,
    incidentSubmissions: Array<SubmissionModel>,
    incidentExpenses: Array<ExpenseModel>,
    allMembershipSubmissions?: Array<SubmissionModel>,
    allMembershipIncidents?: Array<IncidentModel>,
  },
  settings: { hra: Customizations['hra'], ministry: MinistryContext },
): PendingSubmissionModel | UnsavedSubmissionModel | null {
  const existingPendingSubmission: PendingSubmissionModel | undefined =
    incidentSubmissions.filter(isPending)[0]

  let pendingSubmission: PendingSubmissionModel | UnsavedSubmissionModel = existingPendingSubmission

  const submissionType: SubmissionType | null = chooseSubmissionType(
    {
      incident,
      incidentSubmissions: incidentSubmissions,
      incidentExpenses: incidentExpenses,
      allMembershipSubmissions,
      allMembershipIncidents,
    },
    settings
  )

  if (!submissionType) { return null }


  if (!pendingSubmission || submissionType !== existingPendingSubmission.submission_type) {
    // If we haven't yet created the submission or we're changing the type, we will delete and recreate.
    pendingSubmission = buildPendingSubmissionModel(incident, submissionType)
  }

  return pendingSubmission
}

export function buildPendingSubmissionModel(incident: IncidentModel, type: SubmissionType): UnsavedSubmissionModel {
  return {
    membership_id: incident.membership_id,
    incident_id: incident.id,
    submission_type: type
  }
}
